import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import React from 'react'
import showdown from 'showdown'
import backgroundImage from 'assets/images/pdf-background.png'
import Avatar from 'appAssets/appImages/Avatar.png'

interface StyleConfig {
  font: string
  fontSize: number
  textColor: [number, number, number]
  margin: number
  lineHeight: number
}

interface PdfOptions {
  headerImage?: string
  styles?: {
    title?: Partial<StyleConfig>
    description?: Partial<StyleConfig>
    content?: Partial<StyleConfig>
    links?: Partial<StyleConfig>
  }
}

const useDownloadPDF = () => {
  const defaultStyles: Record<'title' | 'description' | 'content' | 'links', StyleConfig> = {
    title: {
      font: 'Inter',
      fontSize: 16,
      textColor: [25, 33, 61],
      margin: 20,
      lineHeight: 1.21,
    },
    description: {
      font: 'Inter',
      fontSize: 14,
      textColor: [63, 63, 70], // #3F3F46
      margin: 20,
      lineHeight: 1.21, // 16.94px / 14px = 1.21
    },
    content: {
      font: 'helvetica',
      fontSize: 12,
      textColor: [71, 84, 103],
      margin: 20,
      lineHeight: 1.5,
    },
    links: {
      font: 'helvetica',
      fontSize: 12,
      textColor: [0, 0, 255],
      margin: 20,
      lineHeight: 1.5,
    },
  }

  const convertMarkdownToHtml = (markdown: string) => {
    const converter = new showdown.Converter({
      simplifiedAutoLink: true,
      strikethrough: true,
      tables: true,
    })
    return converter.makeHtml(markdown)
  }

  const loadImageAsDataUrl = async (imagePath: string): Promise<string | null> => {
    try {
      // If it's already a data URL, return it
      if (imagePath.startsWith('data:')) {
        return imagePath
      }

      // Fetch the image from the URL
      const response = await fetch(imagePath)
      if (!response.ok) {
        console.warn('Failed to load image:', imagePath)
        return null
      }

      const blob = await response.blob()
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
          resolve(reader.result as string)
        }
        reader.onerror = () => {
          reject(new Error('Failed to read image'))
        }
        reader.readAsDataURL(blob)
      })
    } catch (error) {
      console.warn('Error loading image as data URL:', error)
      return null
    }
  }

  const downloadPdf = async (
    contentRef: React.RefObject<HTMLDivElement> | null = null,
    filename = 'download.pdf',
    title?: string,
    description?: string,
    text?: string,
    options: PdfOptions = {}
  ) => {
    try {
      // Load the background image once as data URL
      const backgroundImageDataUrl = await loadImageAsDataUrl(backgroundImage)

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      })

      const pageWidth = pdf.internal.pageSize.width
      const pageHeight = pdf.internal.pageSize.height

      // Merge default styles with provided styles
      const mergedStyles = {
        title: { ...defaultStyles.title, ...(options.styles?.title || {}) },
        description: { ...defaultStyles.description, ...(options.styles?.description || {}) },
        content: { ...defaultStyles.content, ...(options.styles?.content || {}) },
        links: { ...defaultStyles.links, ...(options.styles?.links || {}) },
      }

      const addHeaderImageToPage = (headerHeight: number) => {
        // Helper to add header image if loaded successfully
        if (backgroundImageDataUrl) {
          try {
            pdf.addImage(backgroundImageDataUrl, 'PNG', 0, 0, pageWidth, headerHeight)
          } catch (error) {
            console.warn('Failed to add header image to PDF:', error)
          }
        }
      }

      if (text) {
        // Add header background image
        const headerHeight = 40
        addHeaderImageToPage(headerHeight)

        // Add creation date and website URL
        pdf.setFontSize(8)
        pdf.setTextColor(255, 255, 255) // White color
        pdf.text('www.gotackle.app', 25, 25)

        const currentDate = new Date()
        const formattedDate = `Created: ${
          currentDate.getMonth() + 1
        }/${currentDate.getDate()}/${currentDate.getFullYear()}`
        pdf.text(formattedDate, 25, 30)

        let yPosition = headerHeight + 10

        // Process title
        if (title) {
          pdf.setFontSize(mergedStyles.title.fontSize)
          pdf.setTextColor(...mergedStyles.title.textColor)
          pdf.setFont(mergedStyles.title.font, 'medium')
          const splitTitle = pdf.splitTextToSize(title, pageWidth - mergedStyles.title.margin * 2)
          pdf.text(splitTitle, mergedStyles.title.margin, yPosition)
          yPosition += splitTitle.length * 6 // Each line takes 6mm
        }
        yPosition += 5
        // Process description
        if (description) {
          pdf.setFontSize(mergedStyles.description.fontSize)
          pdf.setTextColor(...mergedStyles.description.textColor)
          pdf.setFont(mergedStyles.description.font, 'medium')
          const splitDescription = pdf.splitTextToSize(
            description,
            pageWidth - mergedStyles.description.margin * 2
          )
          pdf.text(splitDescription, mergedStyles.description.margin, yPosition)
          yPosition += splitDescription.length * 5 // Each line takes 5mm
        }
        yPosition += 5

        // Convert markdown to HTML and extract links
        const htmlContent = convertMarkdownToHtml(text)
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = htmlContent

        const links: { text: string; url: string; startIndex: number; endIndex: number }[] = []
        let plainText = ''
        let listCounters: number[] = []
        let topLevelCounter = 1 // Persistent counter for top-level lists across separate OL blocks

        // Convert HTML to formatted text while preserving original structure and numbering
        const processNode = (node: Node, parentTag?: string) => {
          if (node.nodeType === Node.TEXT_NODE) {
            plainText += node.textContent
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element

            if (element.tagName === 'OL') {
              plainText += '\n'
              // For top-level lists, continue from last number; for nested lists, start from 1
              if (listCounters.length === 0) {
                listCounters.push(topLevelCounter) // Continue top-level numbering
              } else {
                listCounters.push(1) // Nested lists start from 1
              }
              Array.from(node.childNodes).forEach((child) => processNode(child, 'OL'))
              // Update top-level counter before popping
              if (listCounters.length === 1) {
                topLevelCounter = listCounters[0]
              }
              listCounters.pop() // Remove counter when exiting OL
              plainText += '\n'
            } else if (element.tagName === 'UL') {
              plainText += '\n'
              Array.from(node.childNodes).forEach((child) => processNode(child, 'UL'))
              plainText += '\n'
            } else if (element.tagName === 'LI') {
              // Add numbering for ordered lists - newline comes AFTER number
              if (parentTag === 'OL' && listCounters.length > 0) {
                const currentNumber = listCounters[listCounters.length - 1]
                plainText += `${currentNumber}. `
                listCounters[listCounters.length - 1]++ // Increment for next item
              } else {
                plainText += '\n'
              }
              Array.from(node.childNodes).forEach((child) => processNode(child, parentTag))
            } else if (element.tagName === 'H1') {
              plainText += '\n\n'
              Array.from(node.childNodes).forEach((child) => processNode(child))
              plainText += '\n'
            } else if (element.tagName === 'H2' || element.tagName === 'H3') {
              plainText += '\n'
              Array.from(node.childNodes).forEach((child) => processNode(child))
              plainText += '\n'
            } else if (element.tagName === 'P') {
              // Skip leading newline if we're inside a list item to keep content on same line
              if (parentTag !== 'OL' && parentTag !== 'UL' && parentTag !== 'LI') {
                plainText += '\n'
              }
              Array.from(node.childNodes).forEach((child) => processNode(child, parentTag))
              // Always add newline after P, but only space if in list context
              if (parentTag === 'LI') {
                plainText += ' '
              } else {
                plainText += '\n'
              }
            } else if (element.tagName === 'STRONG' || element.tagName === 'B') {
              Array.from(node.childNodes).forEach((child) => processNode(child))
            } else if (element.tagName === 'A') {
              const startIndex = plainText.length
              Array.from(node.childNodes).forEach((child) => processNode(child))
              links.push({
                text: element.textContent || '',
                url: (element as HTMLAnchorElement).href,
                startIndex,
                endIndex: plainText.length,
              })
            } else {
              Array.from(node.childNodes).forEach((child) => processNode(child))
            }
          }
        }

        Array.from(tempDiv.childNodes).forEach((node) => processNode(node))

        // Process main content with better formatting
        pdf.setFontSize(mergedStyles.content.fontSize)
        pdf.setTextColor(...mergedStyles.content.textColor)
        pdf.setFont(mergedStyles.content.font, 'normal')

        const footerHeight = 30 // Height reserved for footer
        const lineHeight = mergedStyles.content.fontSize * 0.5
        const contentStartY = headerHeight + 10 // Starting Y position after header

        // Clean up the plain text and split into lines for better processing
        const cleanedText = plainText
          .replace(/\n\n+/g, '\n\n') // Remove extra line breaks
          .trim()

        const lines = cleanedText.split('\n')
        const processedLines: Array<{ text: string; isNumbered: boolean; isBullet: boolean }> = []

        // Process each line to handle formatting and track line types BEFORE wrapping
        lines.forEach((line) => {
          if (line.trim() === '') {
            processedLines.push({ text: '', isNumbered: false, isBullet: false })
          } else {
            // Check line type BEFORE wrapping
            const isNumbered = /^\d+\.\s/.test(line.trim())
            const isBullet = line.trim().startsWith('•') || line.trim().startsWith('-')

            // Split long lines while preserving formatting flags
            const wrappedLines = pdf.splitTextToSize(
              line,
              pageWidth - mergedStyles.content.margin * 2
            )

            // Mark ALL wrapped segments with the same formatting flags
            wrappedLines.forEach((wrappedLine: string) => {
              processedLines.push({
                text: wrappedLine,
                isNumbered: isNumbered,
                isBullet: isBullet,
              })
            })
          }
        })

        const splitText = processedLines

        // Calculate text positions for links
        let currentPage = 1
        let linesOnCurrentPage = 0

        // Add text and create clickable links
        splitText.forEach((lineObj, index: number) => {
          const line = lineObj.text
          const currentY = yPosition + linesOnCurrentPage * lineHeight

          // Check if we need a new page
          if (currentY > pageHeight - footerHeight) {
            // Add footer to current page
            addFooter(pdf, pageWidth, pageHeight, currentPage)

            // Add new page
            pdf.addPage()
            currentPage++

            // Reset positions for new page
            yPosition = contentStartY
            linesOnCurrentPage = 0

            // Add header background to new page
            addHeaderImageToPage(headerHeight)

            // Add website URL and creation date on new page
            pdf.setFontSize(8)
            pdf.setTextColor(255, 255, 255)
            pdf.text('www.gotackle.app', 25, 25)
            const currentDate = new Date()
            const formattedDate = `Created: ${
              currentDate.getMonth() + 1
            }/${currentDate.getDate()}/${currentDate.getFullYear()}`
            pdf.text(formattedDate, 25, 30)

            // Reset text settings for content
            pdf.setFontSize(mergedStyles.content.fontSize)
            pdf.setTextColor(...mergedStyles.content.textColor)
            pdf.setFont(mergedStyles.content.font, 'normal')
          }

          // Skip empty lines but add spacing
          if (line.trim() === '') {
            linesOnCurrentPage += 0.5 // Add half line spacing for empty lines
            return
          }

          let xPosition = mergedStyles.content.margin
          let displayLine = line.trim() // Remove leading/trailing whitespace

          // Set font weight based on tracked line type (works for wrapped lines too!)
          if (lineObj.isNumbered) {
            // All parts of numbered items should be bold
            pdf.setFont(mergedStyles.content.font, 'bold')
          } else {
            // All other content uses normal weight
            pdf.setFont(mergedStyles.content.font, 'normal')
          }

          // Adjust indentation for bullet points and sub-items
          if (lineObj.isBullet) {
            xPosition = mergedStyles.content.margin + 15 // Indent bullet points
            // Replace various bullet characters with a consistent bullet
            displayLine = displayLine.replace(/^[-•]\s*/, '• ')
          } else if (line.startsWith('   ') || line.startsWith('\t')) {
            // Handle general indented content
            xPosition = mergedStyles.content.margin + 10
          }

          pdf.text(displayLine, xPosition, yPosition + linesOnCurrentPage * lineHeight)

          // Reset to normal font after rendering
          pdf.setFont(mergedStyles.content.font, 'normal')

          // Check for links in this line
          links.forEach((link) => {
            if (displayLine.includes(link.text)) {
              const linkStartInLine = displayLine.indexOf(link.text)
              if (linkStartInLine >= 0) {
                const textWidth = pdf.getTextWidth(displayLine.substring(0, linkStartInLine))
                const linkWidth = pdf.getTextWidth(link.text)

                pdf.link(
                  xPosition + textWidth,
                  yPosition + linesOnCurrentPage * lineHeight - mergedStyles.content.fontSize * 0.3,
                  linkWidth,
                  mergedStyles.content.fontSize,
                  { url: link.url }
                )
              }
            }
          })

          linesOnCurrentPage++
        })

        yPosition += linesOnCurrentPage * lineHeight + 15

        // Add links section with pagination check
        if (links.length > 0) {
          // Check if links section needs a new page
          if (yPosition > pageHeight - footerHeight - 50) {
            addFooter(pdf, pageWidth, pageHeight, currentPage)
            pdf.addPage()
            currentPage++
            yPosition = contentStartY
            addHeaderImageToPage(headerHeight)

            // Add website URL and creation date on new page
            pdf.setFontSize(8)
            pdf.setTextColor(255, 255, 255)
            pdf.text('www.gotackle.app', 25, 25)
            const currentDate = new Date()
            const formattedDate = `Created: ${
              currentDate.getMonth() + 1
            }/${currentDate.getDate()}/${currentDate.getFullYear()}`
            pdf.text(formattedDate, 25, 30)
          }

          pdf.setFont(mergedStyles.links.font, 'bold')
          pdf.text('Sources:', mergedStyles.links.margin, yPosition)
          yPosition += 10

          pdf.setFont(mergedStyles.links.font, 'normal')
          pdf.setTextColor(...mergedStyles.links.textColor)

          links.forEach((link, index) => {
            const linkText = `${index + 1}. ${link.text}`
            const splitLink = pdf.splitTextToSize(
              linkText,
              pageWidth - mergedStyles.links.margin * 2
            )

            // Check if we need a new page for this link
            if (
              yPosition + splitLink.length * mergedStyles.links.fontSize * 0.5 >
              pageHeight - footerHeight
            ) {
              addFooter(pdf, pageWidth, pageHeight, currentPage)
              pdf.addPage()
              currentPage++
              yPosition = contentStartY
              addHeaderImageToPage(headerHeight)

              // Add website URL and creation date on new page
              pdf.setFontSize(8)
              pdf.setTextColor(255, 255, 255)
              pdf.text('www.gotackle.app', 25, 25)
              const currentDate = new Date()
              const formattedDate = `Created: ${
                currentDate.getMonth() + 1
              }/${currentDate.getDate()}/${currentDate.getFullYear()}`
              pdf.text(formattedDate, 25, 30)

              // Reset text settings for links
              pdf.setFont(mergedStyles.links.font, 'normal')
              pdf.setTextColor(...mergedStyles.links.textColor)
            }

            pdf.text(splitLink, mergedStyles.links.margin, yPosition)

            // Make the entire text clickable
            const linkWidth = pdf.getTextWidth(linkText)
            pdf.link(
              mergedStyles.links.margin,
              yPosition - mergedStyles.links.fontSize * 0.3,
              linkWidth,
              mergedStyles.links.fontSize,
              { url: link.url }
            )

            yPosition += splitLink.length * mergedStyles.links.fontSize * 0.5 + 5
          })
        }

        // Add footer to the last page
        addFooter(pdf, pageWidth, pageHeight, currentPage)

        pdf.save(filename)
      } else if (contentRef?.current) {
        // If contentRef is provided, generate a PDF from the content
        const element = contentRef.current
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        })

        const imgData = canvas.toDataURL('image/png')
        const imgWidth = 190 // A4 width
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        let heightLeft = imgHeight
        let position = 10

        // Add title to the first page if provided
        if (title) {
          const marginTop = 20
          pdf.setFontSize(20)
          pdf.text(title, 10, marginTop)
          position = marginTop + 10
        }

        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight)
        heightLeft -= pageHeight

        while (heightLeft > 0) {
          position = heightLeft - imgHeight
          pdf.addPage()
          pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight)
          heightLeft -= pageHeight
        }

        // Add footer with divider
        const footerY = pageHeight - 20
        pdf.setDrawColor(229, 231, 235)
        pdf.line(20, footerY - 10, pageWidth - 20, footerY - 10)

        // Add Avatar image
        const avatarSize = 8 // 8mm diameter
        pdf.addImage(Avatar, 'PNG', 20, footerY - 4, avatarSize, avatarSize)

        // Add "Powered by Coach Logic" text
        pdf.setFontSize(8)
        pdf.setTextColor(128, 128, 128) // Gray color for "Powered by"
        pdf.setFont('helvetica', 'normal')
        pdf.text('Powered by', 20 + avatarSize + 2, footerY - 1)

        pdf.setTextColor(33, 33, 33) // Darker color for "Coach Logic"
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(9)
        pdf.text('Coach Logic', 20 + avatarSize + 2, footerY + 3)

        // Add page number
        pdf.setFont('helvetica', 'normal')
        pdf.setTextColor(128, 128, 128)
        pdf.text(`Page ${pdf.getNumberOfPages()}`, pageWidth - 30, footerY, { align: 'right' })

        pdf.save(filename)
      }
    } catch (error) {
      console.error('Failed to download PDF:', error)
    }
  }

  // Helper function to add footer
  const addFooter = (pdf: jsPDF, pageWidth: number, pageHeight: number, pageNumber: number) => {
    const footerY = pageHeight - 20

    // Add footer divider
    pdf.setDrawColor(229, 231, 235)
    pdf.line(20, footerY - 10, pageWidth - 20, footerY - 10)

    // Add Avatar image
    const avatarSize = 8
    pdf.addImage(Avatar, 'PNG', 20, footerY - 4, avatarSize, avatarSize)

    // Add "Powered by Coach Logic" text
    pdf.setFontSize(8)
    pdf.setTextColor(128, 128, 128)
    pdf.setFont('helvetica', 'normal')
    pdf.text('Powered by', 20 + avatarSize + 2, footerY - 1)

    pdf.setTextColor(33, 33, 33)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(9)
    pdf.text('Coach Logic', 20 + avatarSize + 2, footerY + 3)

    // Add page number
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(128, 128, 128)
    pdf.text(`Page ${pageNumber}`, pageWidth - 30, footerY, { align: 'right' })
  }

  return downloadPdf
}

export default useDownloadPDF
